import { axiosAuthenticatedPost } from '../../api';
import { CoreGraphQLError, requestCoreData, selectSessionClub } from '../../auth/consoleSession';
import { getStorage } from '../../auth/storage';
import { GET_USER, REGISTER_USER } from '../../graphql/userGraphql';
import { CREATE_CLUB } from '../../graphql/clubGraphql';
import { GET_CLUB_BILLING_STATUS, START_CLUB_BILLING_TRIAL } from '../../graphql/billingGraphql';
import { DELETE_IMAGE, UPLOAD_IMAGE } from '../Teams/TeamsService';
import { remBgImageMimeTypesDropzone, sports } from '../../utility/Enum';
import { validateImageFile } from '../../Components/Form/validators';
import { isRegistrationCountry } from './registrationOptions';
import { readRegistrationProgress, saveRegistrationProgress } from './registrationProgress';

export const isClubColor = (value) => /^#[0-9a-f]{6}$/i.test(value || '');
const registrationImageMaxSize = 5 * 1024 * 1024;
export const validateLogo = (file) => !file ||
  (['image/png', 'image/jpeg', 'image/webp'].includes(file.type) && file.size > 0 && file.size <= registrationImageMaxSize);

const nameErrors = (values, names) => {
  const errors = {};
  names.forEach((key) => {
    if (!values[key]?.trim() || values[key].trim().length > 100) errors[key] = 'registration_required_name';
  });
  return errors;
};

export const registrationClubErrors = (values, file) => {
  const errors = nameErrors(values, ['name']);
  if (!isRegistrationCountry(values.country)) errors.country = 'registration_choose_country';
  if (!sports((key) => key).some((sport) => sport.value === values.sport)) errors.sport = 'registration_choose_sport';
  if (!isClubColor(values.primaryColor)) errors.primaryColor = 'registration_invalid_color';
  if (!validateLogo(file)) errors.logo = 'registration_invalid_logo';
  return errors;
};

export const registrationPhotoError = (file) => {
  if (!file) return null;
  const error = validateImageFile(file, remBgImageMimeTypesDropzone, registrationImageMaxSize);
  return { empty: 'user_photo_empty', tooLarge: 'user_photo_too_large', unsupported: 'user_photo_unsupported' }[error] || null;
};

export const registrationErrors = (values, file, photo) => ({
  ...registrationClubErrors(values, file),
  ...nameErrors(values, ['firstName', 'lastName']),
  ...(!values.termsAccepted && { termsAccepted: 'registration_accept_terms' }),
  ...(registrationPhotoError(photo) && { photo: registrationPhotoError(photo) }),
});

const normalized = (value) => String(value || '').trim().toUpperCase();
const matchesCreatedClub = (club, values) => normalized(club.name) === normalized(values.name) &&
  normalized(club.country) === normalized(values.country) && club.sport === values.sport;
const logoFileName = (url) => {
  try { return decodeURIComponent(new URL(url).pathname.split('/').pop()); }
  catch (_) { throw new Error('registration_logo_retry'); }
};

// These errors are emitted before club.save() by Core's createClub or its auth
// wrapper. Unknown execution errors may follow a write and must be reconciled.
const preCreateErrorCodes = new Set(['CLUB_INVALID_INPUT', 'USER_NOT_FOUND', 'Unauthorized', 'Not valid user']);
const isRejectedClubCreation = (error) => error instanceof CoreGraphQLError && (
  (error.data === undefined && error.graphQLErrors.every(({ code, path }) =>
    typeof code === 'string' && code.length > 0 && path === undefined)) ||
  (error.data?.createClub === null && error.graphQLErrors.every(({ code, path }) =>
    path?.length === 1 && path[0] === 'createClub' && preCreateErrorCodes.has(code)))
);

export const createRegistrationService = ({
  uid, coreUserId, storage = localStorage, onProgress = () => {},
  onPhotoSaved = () => {},
  isCurrent = () => getStorage().getItem('firebaseUid') === uid,
  request = (query, variables) => requestCoreData(query, variables, { withoutClub: true, expectedFirebaseUid: uid }),
  saveTheme = (clubId, primaryColor) => axiosAuthenticatedPost(`/theme/${clubId}`, { primaryColor }, { expectedFirebaseUid: uid }),
  upload = (url, file) => fetch(url, { method: 'PUT', body: file }),
  locks = typeof navigator !== 'undefined' ? navigator.locks : null,
}) => {
  let pending = null;
  let uploadedPhoto = null;
  const assertCurrent = () => {
    if (!isCurrent()) throw new Error('session_identity_changed');
  };
  const save = (value) => {
    assertCurrent();
    const progress = saveRegistrationProgress(uid, value, storage);
    onProgress(progress);
    return progress;
  };
  const getMe = async () => {
    assertCurrent();
    const { me } = await request(GET_USER);
    assertCurrent();
    if (!me?.user?._id || String(me.user._id) !== String(coreUserId)) throw new Error('session_identity_changed');
    return me;
  };
  const getOwnedClubs = (me) => selectSessionClub(me).clubs.filter((club) =>
    club.clubAdminUsers?.map(String).includes(String(coreUserId)));
  const uploadImage = async (uploadType, itemId, file, errorKey) => {
    assertCurrent();
    const result = await request(UPLOAD_IMAGE, { uploadType, itemId, fileName: file.name, size: file.size });
    assertCurrent();
    if (typeof result.uploadImage !== 'string' || !result.uploadImage.trim()) throw new Error(errorKey);
    // Core saves the image reference before PUT. Keep the signed URL out of storage.
    const response = await upload(result.uploadImage, file);
    assertCurrent();
    if (!response?.ok) throw new Error(errorKey);
  };
  const loadCreatedClub = async (progress) => {
    const club = getOwnedClubs(await getMe()).find((item) => String(item._id) === String(progress.clubId));
    if (!club) throw new Error('registration_club_unavailable');
    return club;
  };
  const reconcile = async (progress) => {
    const clubs = getOwnedClubs(await getMe()).filter((club) =>
      !(progress.previousClubIds || []).includes(String(club._id)));
    const candidates = clubs.filter((club) => matchesCreatedClub(club, progress.values));
    if (candidates.length !== 1) throw new Error('registration_creation_uncertain');
    return save({ ...progress, clubId: candidates[0]._id, phase: 'branding' });
  };

  const startEligibleTrial = async (clubId) => {
    try {
      assertCurrent();
      const { getClubBillingStatus: billing } = await request(GET_CLUB_BILLING_STATUS, { clubId: String(clubId) });
      assertCurrent();
      if (!billing || String(billing.clubId) !== String(clubId) || typeof billing.billingEnabled !== 'boolean') {
        throw new Error('registration_billing_setup_failed');
      }
      // Core owns rollout, cohort eligibility and trial history. Recovery after
      // an uncertain response observes the existing deadline instead of resetting it.
      if (!billing.billingEnabled) return;
      if (typeof billing.canStartLocalTrial !== 'boolean') throw new Error('registration_billing_setup_failed');
      if (!billing.canStartLocalTrial) return;
      const { startClubBillingTrial: started } = await request(START_CLUB_BILLING_TRIAL, { clubId: String(clubId) });
      assertCurrent();
      if (!started || String(started.clubId) !== String(clubId)) throw new Error('registration_billing_setup_failed');
    } catch (error) {
      assertCurrent();
      throw new Error('registration_billing_setup_failed');
    }
  };

  const brandClub = async (progress, file) => {
    await loadCreatedClub(progress);
    if (!progress.colorSaved) {
      assertCurrent();
      await saveTheme(progress.clubId, progress.values.primaryColor);
      progress = save({ ...progress, colorSaved: true });
    }
    if (progress.logoWanted && progress.logoStatus !== 'saved') {
      if (!file) throw new Error('registration_reselect_logo');
      if (!validateLogo(file)) throw new Error('registration_invalid_logo');
      progress = save({ ...progress, logoStatus: 'pending' });
      await uploadImage('clubLogo', progress.clubId, file, 'registration_logo_retry');
      progress = save({ ...progress, logoStatus: 'saved' });
    }
    // Start only after all required onboarding work succeeds. A failure retains
    // the saved club/branding journal so retry never creates another club.
    await startEligibleTrial(progress.clubId);
    // Registration refreshes the session once after branding and trial activation.
    return { progress };
  };

  const runSubmit = async (values, file, photo) => {
    assertCurrent();
    let progress = readRegistrationProgress(uid, storage);
    if (progress?.phase === 'creating' || progress?.phase === 'uncertain') {
      progress = await reconcile(progress);
    }
    if (progress?.clubId) return brandClub(progress, file);
    if (Object.keys(registrationErrors(values, file, photo)).length) throw new Error('registration_check_fields');
    const me = await getMe();
    if (getOwnedClubs(me).length) throw new Error('registration_access_changed');
    progress = save({
      phase: 'details', previousClubIds: [], logoWanted: Boolean(file), logoStatus: 'pending',
      values: {
        firstName: values.firstName.trim(), lastName: values.lastName.trim(), name: values.name.trim(),
        country: values.country, sport: values.sport, primaryColor: values.primaryColor,
      },
    });
    const registered = await request(REGISTER_USER, {
      firstName: progress.values.firstName, lastName: progress.values.lastName,
    });
    assertCurrent();
    if (String(registered.register?._id) !== String(coreUserId)) throw new Error('session_identity_changed');
    // Save the optional account photo before any club creation. A photo failure
    // leaves setup editable; a successful upload is reused when creation retries.
    if (photo && uploadedPhoto !== photo) {
      try {
        await uploadImage('userImage', coreUserId, photo, 'user_photo_upload_failed');
        uploadedPhoto = photo;
      } catch (error) {
        assertCurrent();
        if (error.message === 'session_identity_changed') throw error;
        const failure = new Error('user_photo_upload_failed');
        failure.field = 'photo';
        throw failure;
      }
    }
    // Check again after saving the profile; another tab may have completed setup.
    const meAfterProfile = await getMe();
    if (getOwnedClubs(meAfterProfile).length) throw new Error('registration_access_changed');
    if (photo && uploadedPhoto === photo) {
      // Use Core's canonical photo URL, which may use a different host from S3.
      // This also completes a retry after PUT succeeded but the account read failed.
      if (typeof meAfterProfile.user.photo !== 'string' || !meAfterProfile.user.photo.trim()) {
        const failure = new Error('user_photo_upload_failed');
        failure.field = 'photo';
        throw failure;
      }
      onPhotoSaved(meAfterProfile.user.photo);
    }
    const latest = readRegistrationProgress(uid, storage);
    if (latest?.clubId || ['creating', 'uncertain'].includes(latest?.phase)) {
      return brandClub(latest.clubId ? latest : await reconcile(latest), file);
    }
    progress = save({ ...progress, phase: 'creating' });
    try {
      const result = await request(CREATE_CLUB, {
        name: progress.values.name, country: progress.values.country, sport: progress.values.sport,
      });
      assertCurrent();
      if (!result.createClub?._id) throw new Error('registration_creation_uncertain');
      progress = save({ ...progress, clubId: result.createClub._id, phase: 'branding' });
    } catch (error) {
      assertCurrent();
      if (isRejectedClubCreation(error)) {
        save({ ...progress, phase: 'details' });
        throw error;
      }
      progress = save({ ...progress, phase: 'uncertain' });
      // No second create is sent after an ambiguous outcome, including after reload.
      // An empty read cannot prove that the original request has stopped executing.
      progress = await reconcile(progress);
    }
    return brandClub(progress, file);
  };

  const runExclusive = (work) => {
    if (pending) return pending;
    pending = Promise.resolve().then(() => locks?.request
      ? locks.request('club-registration:' + uid, { ifAvailable: true }, (lock) => {
        if (!lock) throw new Error('registration_busy');
        return work();
      }) : work()).finally(() => { pending = null; });
    return pending;
  };

  return {
    submit: (values, file, photo) => runExclusive(() => runSubmit(values, file, photo)),
    removeLogo: () => runExclusive(async () => {
      let progress = readRegistrationProgress(uid, storage);
      if (!progress?.clubId) throw new Error('registration_club_unavailable');
      const club = await loadCreatedClub(progress);
      if (club.logo) {
        assertCurrent();
        await request(DELETE_IMAGE, { deleteType: 'clubLogo', itemId: club._id, fileName: logoFileName(club.logo) });
      }
      progress = save({ ...progress, logoWanted: false, logoStatus: 'removed' });
      return progress;
    }),
  };
};
